import { CLIENT_VARIABLE_MAPPING, isClientDocxVariable, } from './client-variable-mapping';
import { isEmployeeGenderAccordDocxVariable, resolveEmployeeGenderAccordValue, resolveEmployeeGrammaticalGender, } from './employee-gender-accord-mapping';
import { EMPLOYEE_VARIABLE_MAPPING, isEmployeeDocxVariable, } from './employee-variable-mapping';
const buildDocxVariableToFieldPaths = (mapping) => {
    const docxToFieldPaths = new Map();
    for (const fieldPath of Object.keys(mapping)) {
        const docxVariable = mapping[fieldPath];
        const fieldPaths = docxToFieldPaths.get(docxVariable) ?? [];
        fieldPaths.push(fieldPath);
        docxToFieldPaths.set(docxVariable, fieldPaths);
    }
    return docxToFieldPaths;
};
const CLIENT_DOCX_TO_FIELD_PATHS = buildDocxVariableToFieldPaths(CLIENT_VARIABLE_MAPPING);
const EMPLOYEE_DOCX_TO_FIELD_PATHS = buildDocxVariableToFieldPaths(EMPLOYEE_VARIABLE_MAPPING);
const isLabeledEnumRecord = (value) => isStrapiComponentRecord(value)
    && typeof value.label === 'string'
    && typeof value.value === 'string';
const formatStrapiScalar = (value) => {
    if (value === null || value === undefined) {
        return undefined;
    }
    if (isLabeledEnumRecord(value)) {
        const trimmedLabel = value.label.trim();
        return trimmedLabel.length === 0 ? undefined : trimmedLabel;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return undefined;
};
const isStrapiComponentRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const unwrapStrapiComponent = (value) => {
    if (Array.isArray(value)) {
        return value[0];
    }
    return value;
};
export const getStrapiFieldValueAtPath = (record, fieldPath, formatFieldValue) => {
    const segments = fieldPath.split('.');
    let current = record;
    for (const segment of segments) {
        if (current === null || current === undefined) {
            return undefined;
        }
        current = unwrapStrapiComponent(current);
        if (current === null || current === undefined) {
            return undefined;
        }
        if (!isStrapiComponentRecord(current)) {
            return undefined;
        }
        current = current[segment];
    }
    const storedValue = formatStrapiScalar(unwrapStrapiComponent(current));
    if (storedValue === undefined) {
        return undefined;
    }
    if (formatFieldValue === undefined) {
        return storedValue;
    }
    return formatFieldValue(fieldPath, storedValue);
};
const matchClientDocxVariable = (descriptor) => {
    for (const candidate of [descriptor.label, descriptor.controlName]) {
        if (candidate.length === 0) {
            continue;
        }
        if (isClientDocxVariable(candidate)) {
            return candidate;
        }
    }
    return null;
};
const matchEmployeeDocxVariable = (descriptor) => {
    for (const candidate of [descriptor.label, descriptor.controlName]) {
        if (candidate.length === 0) {
            continue;
        }
        if (isEmployeeDocxVariable(candidate)) {
            return candidate;
        }
    }
    return null;
};
const matchEmployeeGenderAccordDocxVariable = (descriptor) => {
    for (const candidate of [descriptor.label, descriptor.controlName]) {
        if (candidate.length === 0) {
            continue;
        }
        if (isEmployeeGenderAccordDocxVariable(candidate)) {
            return candidate;
        }
    }
    return null;
};
const EMPLOYEE_TITLE_TO_DOCX_LABEL = {
    'M.': 'Monsieur',
    Mme: 'Madame',
};
const formatEmployeeStoredFieldValue = (fieldPath, storedValue) => {
    if (fieldPath !== 'title') {
        return storedValue;
    }
    if (storedValue in EMPLOYEE_TITLE_TO_DOCX_LABEL) {
        return EMPLOYEE_TITLE_TO_DOCX_LABEL[storedValue];
    }
    return storedValue;
};
const composeFieldValueFormatter = (baseFormatter, customFormatter) => (fieldPath, storedValue) => {
    const baseValue = baseFormatter(fieldPath, storedValue);
    if (customFormatter === undefined) {
        return baseValue;
    }
    return customFormatter(fieldPath, baseValue);
};
const resolveFieldValueForDocxKey = (record, docxKey, docxToFieldPaths, formatFieldValue) => {
    const fieldPaths = docxToFieldPaths.get(docxKey) ?? [];
    for (const fieldPath of fieldPaths) {
        const value = getStrapiFieldValueAtPath(record, fieldPath, formatFieldValue);
        if (value !== undefined) {
            return value;
        }
    }
    return undefined;
};
export const resolveClientEditorVariableValues = (client, variables, formatFieldValue) => {
    const resolvedValues = [];
    for (const variable of variables) {
        const docxKey = matchClientDocxVariable(variable);
        if (docxKey === null) {
            continue;
        }
        const value = client === null
            ? ''
            : resolveFieldValueForDocxKey(client, docxKey, CLIENT_DOCX_TO_FIELD_PATHS, formatFieldValue) ?? '';
        resolvedValues.push({ controlName: variable.controlName, value });
    }
    return resolvedValues;
};
export const resolveClientFieldPathForVariable = (descriptor) => {
    const docxKey = matchClientDocxVariable(descriptor);
    if (docxKey === null) {
        return null;
    }
    const fieldPaths = CLIENT_DOCX_TO_FIELD_PATHS.get(docxKey) ?? [];
    return (fieldPaths[0]) ?? null;
};
export const resolveEmployeeFieldPathForVariable = (descriptor) => {
    const docxKey = matchEmployeeDocxVariable(descriptor);
    if (docxKey === null) {
        return null;
    }
    const fieldPaths = EMPLOYEE_DOCX_TO_FIELD_PATHS.get(docxKey) ?? [];
    return (fieldPaths[0]) ?? null;
};
export const isClientMappedEditorVariable = (descriptor) => matchClientDocxVariable(descriptor) !== null;
export const isEmployeeMappedEditorVariable = (descriptor) => matchEmployeeDocxVariable(descriptor) !== null;
export const isEmployeeGenderAccordEditorVariable = (descriptor) => matchEmployeeGenderAccordDocxVariable(descriptor) !== null;
const resolveGenderAccordValueForEmployee = (employee, docxKey) => {
    if (employee === null) {
        return '';
    }
    const title = getStrapiFieldValueAtPath(employee, 'title');
    const gender = resolveEmployeeGrammaticalGender(title);
    if (gender === null) {
        return '';
    }
    return resolveEmployeeGenderAccordValue(docxKey, gender);
};
export const resolveEmployeeEditorVariableValues = (employee, variables, formatFieldValue) => {
    const resolvedValues = [];
    for (const variable of variables) {
        const fieldDocxKey = matchEmployeeDocxVariable(variable);
        if (fieldDocxKey !== null) {
            const value = employee === null
                ? ''
                : resolveFieldValueForDocxKey(employee, fieldDocxKey, EMPLOYEE_DOCX_TO_FIELD_PATHS, composeFieldValueFormatter(formatEmployeeStoredFieldValue, formatFieldValue))
                    ?? '';
            resolvedValues.push({ controlName: variable.controlName, value });
            continue;
        }
        const accordDocxKey = matchEmployeeGenderAccordDocxVariable(variable);
        if (accordDocxKey === null) {
            continue;
        }
        resolvedValues.push({
            controlName: variable.controlName,
            value: resolveGenderAccordValueForEmployee(employee, accordDocxKey),
        });
    }
    return resolvedValues;
};
//# sourceMappingURL=resolve-editor-variable-values.js.map